import { AxiosResponse, AxiosError } from "axios";
import api from "@services/axios/apiManager";
import { useLineAuth } from "@utils/store/auth/useLineAuth";
import { useOrder } from "@utils/store/cart/useOrder";
import { getDraftErrorMessage, isDraftAddressMismatch } from "@utils/api/draftErrorMessage";
import toast from "react-hot-toast";
import { isPhoneNotVerifiedError, PHONE_NOT_VERIFIED_TOAST, reopenCheckoutOtp } from "@screen/Kiosk/phoneNotVerified";

// 跨地址 race：customer_delivery_id 屬於別的地址時 BE 回 400。退回不帶 id 重試一次，
// 讓 BE 依內容（用途+樓層+電梯+備註）content-match upsert 到 draft 實際綁定的地址。
// 沒有 customer_delivery_id（如 commodity）或本就不是地址不符 → 回傳原錯誤交由呼叫端處理。
const withoutCustomerDeliveryId = (body: ItemBody): ItemBody => {
  const { customer_delivery_id, ...rest } = body;
  return rest;
};

type ProductItemType = "gas" | "commodity";

interface ItemBody {
  product_id?: string;
  quantity?: number;
  qty?: number;
  [key: string]: any;
}

interface AddProductItemParams {
  xContext: string;
  jwtToken: string;
  type: ProductItemType;
  draftId: string;
  body: ItemBody;
}

interface updateProductItemParams {
  xContext: string;
  jwtToken: string;
  type: ProductItemType;
  draftId: string;
  body: ItemBody;
  lineId: string;
}

interface delProductItemParams {
  xContext: string;
  jwtToken: string;
  type: ProductItemType;
  draftId: string;
  lineId: string;
}

const typeTag: Record<ProductItemType, string> = {
  gas: "gas-items",
  commodity: "commodity-items",
};
//@ 單筆"新增"商品
export const addProductItem = async ({ type, draftId, body, jwtToken, xContext }: AddProductItemParams) => {
  const apiUrl = `/app-api/platform-order-drafts/${draftId}/${typeTag[type]}`;
  const headers = { Authorization: `Bearer ${jwtToken}`, "X-Context": xContext };

  try {
    const res: AxiosResponse = await api.post(apiUrl, body, { headers });
    return { state: "success" as const, data: res.data, mes: "新增商品成功" };
  } catch (error) {
    const axiosError = error as AxiosError;
    // 跨地址 race → 退回不帶 customer_delivery_id 重試一次
    if (isDraftAddressMismatch(axiosError) && body.customer_delivery_id != null) {
      try {
        const res: AxiosResponse = await api.post(apiUrl, withoutCustomerDeliveryId(body), { headers });
        return { state: "success" as const, data: res.data, mes: "新增商品成功" };
      } catch (retryError) {
        const axiosRetryError = retryError as AxiosError;
        console.error("新增商品失敗(retry):", axiosRetryError);
        toast.error(getDraftErrorMessage(axiosRetryError, "新增商品失敗"), { id: "add-product-item-error" });
        return { state: "error" as const, error: axiosRetryError };
      }
    }
    console.error("新增商品失敗:", axiosError);
    toast.error(getDraftErrorMessage(axiosError, "新增商品失敗"), { id: "add-product-item-error" });
    return { state: "error" as const, error: axiosError };
  }
};

//@ 單筆"更新"商品
export const updateProductItem = async ({ type, draftId, body, lineId, jwtToken, xContext }: updateProductItemParams) => {
  const apiUrl = `/app-api/platform-order-drafts/${draftId}/${typeTag[type]}/${lineId}`;
  const headers = { Authorization: `Bearer ${jwtToken}`, "X-Context": xContext };

  try {
    const res: AxiosResponse = await api.put(apiUrl, body, { headers });
    return { state: "success" as const, data: res.data, mes: "更新商品成功" };
  } catch (error) {
    const axiosError = error as AxiosError;
    // 跨地址 race → 退回不帶 customer_delivery_id 重試一次
    if (isDraftAddressMismatch(axiosError) && body.customer_delivery_id != null) {
      try {
        const res: AxiosResponse = await api.put(apiUrl, withoutCustomerDeliveryId(body), { headers });
        return { state: "success" as const, data: res.data, mes: "更新商品成功" };
      } catch (retryError) {
        const axiosRetryError = retryError as AxiosError;
        console.error("更新商品失敗(retry):", axiosRetryError);
        toast.error(getDraftErrorMessage(axiosRetryError, "更新商品失敗"), { id: "update-product-item-error" });
        return { state: "error" as const, error: axiosRetryError };
      }
    }
    console.error("更新商品失敗:", axiosError);
    toast.error(getDraftErrorMessage(axiosError, "更新商品失敗"), { id: "update-product-item-error" });
    return { state: "error" as const, error: axiosError };
  }
};

//@ 單筆"刪除"商品
export const delProductItem = async ({ type, draftId, lineId, jwtToken, xContext }: delProductItemParams) => {
  const apiUrl = `/app-api/platform-order-drafts/${draftId}/${typeTag[type]}/${lineId}`;

  try {
    const res: AxiosResponse = await api.delete(apiUrl, {
      headers: {
        Authorization: `Bearer ${jwtToken}`,
        "X-Context": xContext,
      },
    });
    return { state: "success" as const, data: res.data, mes: "刪除商品成功" };
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("刪除商品失敗:", axiosError);
    toast.error(getDraftErrorMessage(axiosError, "刪除商品失敗"));
    return { state: "error" as const, error: axiosError };
  }
};

//@ 刪除整個草稿訂單
export const delDraftOrder = async ({ draftId, jwtToken, xContext }: { draftId: string; jwtToken: string; xContext: string }) => {
  const apiUrl = `/app-api/platform-order-drafts/${draftId}`;

  try {
    const res: AxiosResponse = await api.delete(apiUrl, {
      headers: {
        Authorization: `Bearer ${jwtToken}`,
        "X-Context": xContext,
      },
    });
    return { state: "success" as const, data: res.data, mes: "刪除草稿成功" };
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("刪除草稿失敗:", axiosError);
    return { state: "error" as const, error: axiosError };
  }
};

//@ 更新整個草稿訂單資訊
export const updateDraftOrder = async ({
  draftId,
  body,
  jwtToken,
  xContext,
}: {
  draftId: string;
  body: any;
  jwtToken: string;
  xContext: string | undefined;
}) => {
  const apiUrl = `/app-api/platform-order-drafts/${draftId}`;

  try {
    const res: AxiosResponse = await api.put(apiUrl, body, {
      headers: {
        Authorization: `Bearer ${jwtToken}`,
        "X-Context": xContext,
      },
    });
    return { state: "success" as const, data: res.data, mes: "更新草稿成功" };
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("更新訂單失敗:", axiosError);
    toast.error(getDraftErrorMessage(axiosError, "更新訂單失敗"));
    return { state: "error" as const, error: axiosError };
  }
};

//@ 送出訂單 (正式下單)
export const submitOrder = async ({
  body,
  jwtToken,
  xContext,
}: {
  body: { draft_id: string; client_request_id: string; customer_coupon_id?: string | null };
  jwtToken: string;
  xContext: string;
}) => {
  const apiUrl = `/app-api/platform-orders`;

  try {
    const res: AxiosResponse = await api.post(apiUrl, body, {
      headers: {
        Authorization: `Bearer ${jwtToken}`,
        "X-Context": xContext,
      },
    });
    return { state: "success" as const, data: res.data, mes: "訂單送出成功" };
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("訂單送出失敗:", axiosError);
    if (isPhoneNotVerifiedError(axiosError)) {
      // 後端擋「手機未驗證」：兩個結算頁共用這支送單，在這裡統一把驗證卡叫出來（呼叫端照常 setSubmitting(false)）
      reopenCheckoutOtp();
      toast.error(PHONE_NOT_VERIFIED_TOAST);
    } else {
      toast.error("訂單送出失敗");
    }
    return { state: "error" as const, error: axiosError };
  }
};
